/**
 * /policies/travel/advance-reconciliation — Session 3c PR 23.
 *
 * Owning agent: Rawatib. Special-shape: single-page form (NOT a list).
 * M3X.TRAVEL default 70/30 advance/reconciliation ratio.
 */

import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3cApi } from "../lib/session3c-api";
import type { AdvanceReconciliationItem } from "../lib/session3c-api";
import { showAlertTrace } from "./_session3aHelpers";
import {
  PolicyActionsRow,
  Session3cPolicyShell,
  buildOverrideStackTrace,
} from "./_session3cShell";

export function TravelAdvanceReconciliationPolicyPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const query = useQuery({
    queryKey: ["s3c-advance-reconciliation", legalEntityId],
    queryFn: () => session3cApi.getAdvanceReconciliation(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  if (gate) return gate;

  const cfg: AdvanceReconciliationItem | null =
    query.data && "advance_pct" in (query.data as object)
      ? (query.data as AdvanceReconciliationItem)
      : null;

  return (
    <Session3cPolicyShell
      title={t("policies.travel_advance.title", "Travel Advance / Reconciliation")}
      subtitle={t(
        "policies.travel_advance.subtitle",
        "Default advance % · reconciliation deadline · auto-deduct policy.",
      )}
      agent="rawatib"
      briefContext={{
        total: 1,
        attention: 0,
        critical: 0,
        top_kind: t("policies.travel_advance.title", "Advance / Reconciliation"),
        window_label: cfg
          ? `${cfg.advance_pct.value}% advance · ${cfg.reconciliation_deadline_days}d deadline`
          : "—",
      }}
      actionsRow={
        <PolicyActionsRow
          domain="travel_advance_reconciliation"
          newLabel={t("policies.travel_advance.cta.edit", "Edit Configuration")}
          onNewClick={() =>
            showAlertTrace(
              t(
                "policies.travel_advance.cta.note",
                "Edit drawer: advance_pct · reconciliation_deadline_days · auto_deduct_if_overdue. Wires with the M3X.TRAVEL pack.",
              ),
            )
          }
        />
      }
    >
      <InlineAISuggestion
        agent="rawatib"
        icon={<Sparkles className="h-4 w-4" />}
        severity="info"
        headline={t(
          "policies.travel_advance.suggestion.headline",
          "M3X.TRAVEL default: 70% advance · 30% on reconciliation.",
        )}
        body={t(
          "policies.travel_advance.suggestion.body",
          "Lower advance % means lower recovery exposure; higher % means easier traveler cash-flow but more reconciliation risk. Auto-deduct from salary closes the loop when reconciliation is overdue.",
        )}
        primaryAction={{
          label: t("policies.travel_advance.suggestion.queue", "Open reconciliation queue"),
          onClick: () => window.location.assign("/operations/travel/reconciliation"),
        }}
        suggestionKey="advance-reconciliation"
      />

      {query.isLoading && <Loading />}
      {query.isError && <ErrorPanel message={(query.error as Error).message} />}

      {cfg && (
        <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                <th className="px-3 py-2">{t("policies.travel_advance.col.parameter", "Parameter")}</th>
                <th className="px-3 py-2">{t("policies.travel_advance.col.value", "Active value")}</th>
                <th className="px-3 py-2">{t("policies.travel_advance.col.source", "Source")}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2 font-medium">
                  {t("policies.travel_advance.row.advance", "Advance %")}
                </td>
                <td className="px-3 py-2">
                  <TraceableValue
                    onTraceClick={() =>
                      showAlertTrace(
                        buildOverrideStackTrace(
                          t("policies.travel_advance.row.advance", "Advance %"),
                          cfg.advance_pct,
                        ),
                      )
                    }
                  >
                    <span className="font-mono font-semibold">
                      {cfg.advance_pct.value}%
                    </span>
                  </TraceableValue>
                </td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">
                  {cfg.advance_pct.active_layer}
                </td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.travel_advance.row.deadline", "Reconciliation deadline (days)")}</td>
                <td className="px-3 py-2 font-mono">{cfg.reconciliation_deadline_days}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.travel_advance.row.auto_deduct", "Auto-deduct if overdue")}</td>
                <td className="px-3 py-2 text-xs">{cfg.auto_deduct_if_overdue ? "yes" : "no"}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Session3cPolicyShell>
  );
}
